<template>
  <RuesBoxSize
    :size="props.item?.width"
    class="grid grid-cols-12 gap-4 usp-carousel"
  >
    <UiHeadline
      type="h2"
      design="h2"
      class="col-span-12"
    >
      {{ props.item?.title }}
    </UiHeadline>
    <div
      v-if="!props.skeleton"
      class="col-span-12 relative"
    >
      <UiCarousel
        class="embla-usp"
        :slide-size="SLIDE_SIZE_MOBILE"
        :slide-count="total"
        :ssr-breakpoints="ssrBreakpoints"
        :show-arrows="total > 1"
        :options="carouselOptions"
        :accessibility="accessibilityOptions"
      >
        <div
          v-for="(usp, index) in usps"
          :key="index"
          class="embla__slide px-[9px]"
        >
          <div class="border border-neutral-100 rounded-xl px-5 py-5 flex flex-col gap-3 h-full">
            <span class="min-h-[46px] min-w-[42px] inline-block">
              <UiIcon
                :src="usp.icon"
                :width="usp.width"
                :height="usp.height"
                class="fill-petrol-500 flex align-center"
              />
            </span>
            <div class="text-xl font-semibold">
              {{ usp.headline }}
            </div>
            <div>
              {{ usp.text }}
            </div>
          </div>
        </div>

        <template
          v-if="total > 1"
          #prev
        >
          <span aria-hidden="true" />
        </template>
        <template
          v-if="total > 1"
          #next
        >
          <span aria-hidden="true" />
        </template>
      </UiCarousel>
    </div>
    <div
      v-show="props.skeleton"
      class="col-span-12 flex flex-nowrap gap-x-[18px] w-full overflow-hidden"
    >
      <RuesSkeletonUspCard
        design="card"
        class="w-[77.3%] md:w-[42%] lg:w-[33.3333%]"
      />
      <RuesSkeletonUspCard
        design="card"
        class="md:w-[42%] lg:w-[33.3333%] hidden md:block"
      />
      <RuesSkeletonUspCard
        design="card"
        class="lg:w-[33.3333%] hidden lg:block"
      />
      <RuesSkeletonUspCard
        design="cardsmall"
        class="w-[19.7%] md:w-[10.8%] block lg:hidden"
      />
    </div>
  </RuesBoxSize>
</template>

<script setup lang="ts">
import type { EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'
import type { SsrOptionsType } from 'embla-carousel-ssr'
import { BREAKPOINT_LG_MIN_QUERY } from '~/utils/breakpoints'

const SLIDE_SIZE_MOBILE = 76.9
const SLIDE_SIZE_TABLET = 43.5
const SLIDE_SIZE_DESKTOP = 33.333

const { translate: t } = useTranslations('RuesUSP')

interface CarouselItem {
  title?: string
  width?: number
  headline?: string
  text?: string
  headline2?: string
  text2?: string
  headline3?: string
  text3?: string
}

interface Props {
  skeleton?: boolean
  item?: CarouselItem | null
}

const props = withDefaults(defineProps<Props>(), {
  skeleton: false,
  item: null
})

const usps = computed(() => [
  {
    icon: '/svg/usp/location.svg',
    width: 42,
    height: 46,
    headline: props.item?.headline,
    text: props.item?.text
  },
  {
    icon: '/svg/usp/service.svg',
    width: 46,
    height: 46,
    headline: props.item?.headline2,
    text: props.item?.text2
  },
  {
    icon: '/svg/usp/like.svg',
    width: 48,
    height: 46,
    headline: props.item?.headline3,
    text: props.item?.text3
  }
])

const total = computed(() => usps.value.length)

const ssrBreakpoints = computed(() => {
  const n = Math.max(total.value, 0)
  return {
    '(min-width: 768px)': { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_TABLET) },
    [BREAKPOINT_LG_MIN_QUERY]: { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_DESKTOP) }
  } as NonNullable<SsrOptionsType['breakpoints']>
})

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: false
}))

const accessibilityOptions = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: props.item?.title || t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => {
    const headline = usps.value[first]?.headline
    return headline
      ? t.value('slide_aria_label_with_headline', { headline, current: first + 1, total: total.value })
      : t.value('slide_aria_label', { current: first + 1, total: total.value })
  },
  liveRegionContent: (_grouped, first) => {
    const headline = usps.value[first]?.headline
    return headline
      ? t.value('live_region_content_with_headline', { headline, current: first + 1, total: total.value })
      : t.value('live_region_content', { current: first + 1, total: total.value })
  },
  rootNode: emblaRoot => emblaRoot.parentElement
}))
</script>

<style src="./css/RuesUSPCarousel.css" scoped></style>
